import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import usePageMeta from "../lib/usePageMeta";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy | American Asset Recovery Agency",
    description: "How American Asset Recovery Agency collects, uses, and protects your personal information. We never sell your data.",
    canonical: "https://americanassetrecoveryagency.com/privacy",
  });
  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="privacy-page">
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/" className="text-sm text-[#475569] hover:text-[#FF008F]" data-testid="back-home">← Back to site</Link>

          <p className="mt-8 text-sm font-bold uppercase tracking-[0.18em] text-[#FF008F]">Privacy Policy</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] leading-tight mt-3">
            Your privacy matters.
          </h1>
          <p className="mt-3 text-sm text-[#64748B]">Last updated: February 2026</p>

          <div className="mt-10 prose-content space-y-8 text-[#334155] leading-relaxed">
            <Section title="1. Who we are">
              <p>American Asset Recovery Agency ("we," "us," "our") helps homeowners recover surplus funds from foreclosure proceedings. This Privacy Policy explains what information we collect, how we use it, and the choices you have.</p>
            </Section>

            <Section title="2. Information we collect">
              <p>When you submit our claim or contact forms, we collect:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your name, email address, and phone number</li>
                <li>Property address, county, state, and approximate foreclosure date (claim form only)</li>
                <li>Any optional details or messages you choose to share</li>
                <li>Standard server logs (timestamp, IP address)</li>
              </ul>
              <p>We do <strong>not</strong> collect Social Security numbers, banking details, or payment information through this website.</p>
            </Section>

            <Section title="3. How we use your information">
              <ul className="list-disc pl-6 space-y-1">
                <li>To respond to your inquiry and evaluate a potential surplus funds claim on your behalf.</li>
                <li>To contact you by phone or email regarding your case.</li>
                <li>To comply with applicable laws and court filings if you choose to engage our services.</li>
              </ul>
              <p>We do <strong>not</strong> sell, rent, or trade your personal information to third parties for marketing purposes.</p>
            </Section>

            <Section title="4. How we share your information">
              <p>We share information only with: (a) our internal team and contracted recovery specialists, (b) courts and government agencies when filing a petition on your behalf (with your written authorization), and (c) service providers that support our operations (such as secure email and database providers), bound by confidentiality obligations.</p>
            </Section>

            <Section title="5. SMS &amp; text messaging privacy">
              <p>This section explains how we handle information related to our SMS (text messaging) program. It applies in addition to the rest of this Privacy Policy.</p>
              <p><strong>Information we collect when you opt in to SMS:</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your mobile phone number</li>
                <li>Your consent to receive SMS messages from us</li>
                <li>Your email address and basic contact information (when provided)</li>
                <li>Your messaging history with us (delivery status, timestamps, opt-in / opt-out events)</li>
              </ul>
              <p><strong>How we collect it:</strong> Directly from you — for example, when you complete a form on this website, submit a claim, or contact us by phone or email.</p>
              <p><strong>How we use it:</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>To send the SMS messages you have opted in to receive (case updates, appointment reminders, document reminders, and general two-way communication about your surplus funds claim).</li>
                <li>To provide updates or relevant content based on your preferences.</li>
                <li>To operate and improve our business.</li>
              </ul>
              <p><strong>Your choices and controls:</strong> You may opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message we send you. Reply <strong>HELP</strong> for support. Full opt-out details are in our <Link to="/terms" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">Terms of Service</Link>.</p>
              <p><strong>No third-party sharing of SMS consent data:</strong> We do <strong>not</strong> share your personal information, phone number, or SMS consent opt-in data with third parties or affiliates for marketing or promotional purposes. Consent to receive SMS is <strong>not</strong> transferred or sold to any external party under any circumstance.</p>
              <p><strong>When we may disclose SMS-related information:</strong> to our affiliated companies; to third-party service providers, business advisors, or consultants who help us operate our SMS program (such as our messaging platform); in connection with a merger, acquisition, reorganization, restructuring, financing transaction, or sale of assets; as required by law or administrative order; and to assert or defend against legal claims.</p>
            </Section>

            <Section title="6. Consumer contact method">
              <p><strong>We provide a clear contact method for message recipients who need help or assistance from the sender.</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Support Contact:</strong> Recipients can contact the message sender using the customer support details provided in the message.</li>
                <li><strong>Purpose:</strong> This contact method is available for questions, help requests, or assistance related to the message received.</li>
                <li><strong>Clarity:</strong> Contact information is presented clearly so recipients know how to reach the sender when needed.</li>
              </ul>
              <p>You can reach our support team at <a href="mailto:recover@americanassetrecoveryagency.com" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">recover@americanassetrecoveryagency.com</a> or <a href="tel:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a>. Text <strong>HELP</strong> to <a href="sms:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a> for SMS support.</p>
            </Section>

            <Section title="7. Data security">
              <p>We use industry-standard safeguards — encrypted connections (HTTPS), restricted access controls, and secure cloud infrastructure — to protect your information. No system is 100% secure, but we treat your data with the care you'd expect from a financial-services firm.</p>
            </Section>

            <Section title="8. Your choices &amp; rights">
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Access / correction:</strong> Request a copy of the information we hold about you, or ask us to correct it.</li>
                <li><strong>Deletion:</strong> Ask us to delete your information (subject to any legal retention obligations).</li>
                <li><strong>Opt-out:</strong> Unsubscribe from communications at any time by replying <strong>STOP</strong> to a text or "unsubscribe" to an email.</li>
              </ul>
              <p>To exercise any of these, email <a href="mailto:recover@americanassetrecoveryagency.com" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">recover@americanassetrecoveryagency.com</a>.</p>
            </Section>

            <Section title="9. Cookies">
              <p>This website uses minimal essential cookies to keep the site working (for example, an admin session cookie). We do not use third-party advertising or tracking cookies.</p>
            </Section>

            <Section title="10. Children">
              <p>This site is intended for adults 18+ and is not directed to children under 13. We do not knowingly collect information from minors.</p>
            </Section>

            <Section title="11. Updates to this policy">
              <p>We may periodically update this Privacy Policy. If we make material changes that have a substantive and adverse impact on your privacy, we will provide notice on this website prior to the change becoming effective. We encourage you to periodically review this page for the latest information about our privacy practices.</p>
            </Section>

            <Section title="12. Contact">
              <p>Questions about this Privacy Policy, or SMS-related questions?</p>
              <p>
                American Asset Recovery Agency<br />
                Email: <a href="mailto:recover@americanassetrecoveryagency.com" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">recover@americanassetrecoveryagency.com</a><br />
                Phone: <a href="tel:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a><br />
                SMS support: Text <strong>HELP</strong> to <a href="sms:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a>
              </p>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif-display text-2xl text-[#0F172A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
